import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, Select, MenuItem,
  Snackbar, Alert, CircularProgress, FormControl,
  InputLabel, Card, CardContent, Chip, Fade, useMediaQuery,
  Avatar, Container, Skeleton
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Clock, Coffee, LogOut, LogIn, CheckCircle,
  Building, Briefcase, MapPin, Play, Pause, GalleryHorizontalEnd, Lock
} from 'lucide-react';
import { format } from 'date-fns';
import api from '../services/api';

const ClockInOutPage = () => {
  // Estados principais
  const [hotelId, setHotelId] = useState('');
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [currentStatus, setCurrentStatus] = useState(null);
  const [lastAction, setLastAction] = useState(null);
  const [recentLogs, setRecentLogs] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [showSuccess, setShowSuccess] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [lockedHotelId, setLockedHotelId] = useState(null); // 🔒 Hotel travado vindo do backend
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Atualizar relógio em tempo real
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Carregar dados iniciais
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setLoadingData(true);
    try {
      await Promise.all([
        loadHotels(),
        loadClockStatus(),
        loadRecentLogs()
      ]);
      
      // Carregar hotel salvo apenas se não estiver travado
      const savedHotelId = localStorage.getItem('selected_hotel_id');
      if (savedHotelId && !lockedHotelId) {
        setHotelId(savedHotelId);
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
    } finally {
      setLoadingData(false);
    }
  };

  const loadHotels = async () => {
    try {
      const response = await api.get('/hotels');
      setHotels(response.data);
    } catch (error) {
      console.error('Error loading hotels:', error);
      showSnackbar('Error loading hotels', 'error');
    }
  };

  const loadClockStatus = async () => {
    try {
      const response = await api.get('/web/clock/status');
      if (response.data.success) {
        setCurrentStatus(response.data.status);
        setLastAction(response.data.last_action);
        
        // 🔒 SIMPLIFICADO: Backend já retorna o locked_hotel_id
        const locked = response.data.locked_hotel_id;
        setLockedHotelId(locked);
        
        // Se hotel estiver travado, definir ele como selecionado
        if (locked) {
          setHotelId(locked);
          localStorage.setItem('selected_hotel_id', locked);
        }
      }
    } catch (error) {
      console.error('Error loading status:', error);
    }
  };

  const loadRecentLogs = async () => {
    try {
      const response = await api.get('/web/clock/recent', {
        params: { limit: 5 }
      });
      if (response.data.success) {
        setRecentLogs(response.data.logs);
      }
    } catch (error) {
      console.error('Error loading recent logs:', error);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleClockAction = async (action) => {
    if (!hotelId) {
      showSnackbar('Please select a hotel', 'error');
      return;
    }

    setLoading(true);
    setActionType(action);

    try {
      const response = await api.post('/web/clock', {
        hotel_id: hotelId,
        type: action
      });

      if (response.data.success) {
        // Salvar hotel selecionado
        localStorage.setItem('selected_hotel_id', hotelId);

        // Atualizar dados (o backend já gerencia o travamento)
        await Promise.all([
          loadClockStatus(),
          loadRecentLogs()
        ]);
        
        // Animação de sucesso
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 2000);

        showSnackbar(response.data.message, 'success');
      }
    } catch (error) {
      console.error('Clock action error:', error);
      const errorMessage = error.response?.data?.message || 'An error occurred';
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
      setActionType(null);
    }
  };

  const getActionColor = (action) => {
    const colors = {
      clock_in: '#4caf50',
      break_out: '#ff9800',
      break_in: '#2196f3',
      clock_out: '#f44336'
    };
    return colors[action] || '#666';
  };

  const getActionIcon = (action, size = 24) => {
    const icons = {
      clock_in: <LogIn size={size} />,
      break_out: <Pause size={size} />,
      break_in: <Play size={size} />,
      clock_out: <LogOut size={size} />
    };
    return icons[action] || <Clock size={size} />;
  };

  const getStatusInfo = () => {
    const statusConfig = {
      working: {
        label: 'Working',
        color: '#4caf50',
        icon: <Briefcase size={20} />,
        bg: '#e8f5e9'
      },
      on_break: {
        label: 'On Break',
        color: '#ff9800',
        icon: <Coffee size={20} />,
        bg: '#fff3e0'
      },
      not_clocked_in: {
        label: 'Not Clocked In',
        color: '#9e9e9e',
        icon: <Clock size={20} />,
        bg: '#f5f5f5'
      }
    };
    return statusConfig[currentStatus] || statusConfig.not_clocked_in;
  };

  // 🔒 Verificar se o hotel está travado
  const isHotelLocked = () => {
    return lockedHotelId !== null;
  };

  if (loadingData) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 4 }} />
          <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 3 }} />
        </Box>
      </Container>
    );
  }

  const statusInfo = getStatusInfo();

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: '#f5f7fa',
      py: { xs: 2, sm: 4 }
    }}>
      <Container maxWidth="md">
        {/* Header com Relógio */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            mb: 3,
            borderRadius: 4,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            textAlign: 'center'
          }}
        >
          <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.1)' }} />
          <Box sx={{ position: 'absolute', bottom: -30, left: -30, width: 150, height: 150, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.05)' }} />
          
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold" mb={2}>
              Clock System
            </Typography>
            
            <Typography variant={isMobile ? "h2" : "h1"} fontWeight="bold" sx={{ mb: 1, fontSize: { xs: '3rem', sm: '4rem' } }}>
              {format(currentTime, 'HH:mm:ss')}
            </Typography>
            
            <Typography variant="h6" sx={{ opacity: 0.9 }}>
              {format(currentTime, 'EEEE, MMMM d, yyyy')}
            </Typography>

            {/* Status Badge */}
            <Chip
              icon={statusInfo.icon}
              label={statusInfo.label}
              sx={{
                mt: 3,
                bgcolor: 'white',
                color: statusInfo.color,
                fontWeight: 'bold',
                fontSize: { xs: '0.9rem', sm: '1rem' },
                px: 2,
                py: 3,
                '& .MuiChip-icon': { color: statusInfo.color }
              }}
            />
          </Box>
        </Paper>

        {/* Painel de Ações */}
        <Paper elevation={2} sx={{ p: { xs: 3, sm: 4 }, mb: 3, borderRadius: 3 }}>
          {/* Seleção de Hotel */}
          <FormControl 
            fullWidth 
            sx={{ mb: 4 }}
            disabled={isHotelLocked()} // 🔒 Desabilitar se hotel estiver travado
          >
            <InputLabel id="hotel-label">
              {isHotelLocked() ? '🔒 Hotel (Locked)' : 'Select Hotel'}
            </InputLabel>
            <Select
              labelId="hotel-label"
              value={hotelId}
              onChange={(e) => setHotelId(e.target.value)}
              label={isHotelLocked() ? '🔒 Hotel (Locked)' : 'Select Hotel'}
              disabled={isHotelLocked()} // 🔒 Desabilitar se hotel estiver travado
              startAdornment={
                isHotelLocked() ? (
                  <Lock size={20} style={{ marginLeft: 8, marginRight: 4, color: '#ff9800' }} />
                ) : (
                  <Building size={20} style={{ marginLeft: 8, marginRight: 4, color: '#667eea' }} />
                )
              }
              sx={{
                borderRadius: 2,
                bgcolor: isHotelLocked() ? '#fff3e0' : 'white', // 🔒 Cor diferente quando travado
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: isHotelLocked() ? '#ff9800' : undefined, // 🔒 Borda laranja quando travado
                  borderWidth: isHotelLocked() ? 2 : 1
                }
              }}
            >
              {hotels.map((hotel) => (
                <MenuItem key={hotel.id} value={hotel.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {hotel.name}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>


          {/* Botões de Ação */}
          <Box 
            sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2 
            }}
          >
            <Button
              variant="contained"
              onClick={() => handleClockAction('clock_in')}
              disabled={loading || !hotelId}
              startIcon={loading && actionType === 'clock_in' ? <CircularProgress size={20} color="inherit" /> : <LogIn size={22} />}
              sx={{
                bgcolor: '#4caf50',
                color: 'white',
                py: 2.5,
                borderRadius: 2,
                fontWeight: 'bold',
                fontSize: { xs: '0.95rem', sm: '1.1rem' },
                '&:hover': { bgcolor: '#45a049' },
                '&:disabled': { bgcolor: '#e0e0e0' },
                transition: 'all 0.3s',
                boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)'
              }}
            >
              Clock In
            </Button>

            <Button
              variant="contained"
              onClick={() => handleClockAction('break_out')}
              disabled={loading || !hotelId}
              startIcon={loading && actionType === 'break_out' ? <CircularProgress size={20} color="inherit" /> : <Pause size={22} />}
              sx={{
                bgcolor: '#ff9800',
                color: 'white',
                py: 2.5,
                borderRadius: 2,
                fontWeight: 'bold',
                fontSize: { xs: '0.95rem', sm: '1.1rem' },
                '&:hover': { bgcolor: '#f57c00' },
                '&:disabled': { bgcolor: '#e0e0e0' },
                transition: 'all 0.3s',
                boxShadow: '0 4px 12px rgba(255, 152, 0, 0.3)'
              }}
            >
              Break Out
            </Button>

            <Button
              variant="contained"
              onClick={() => handleClockAction('break_in')}
              disabled={loading || !hotelId}
              startIcon={loading && actionType === 'break_in' ? <CircularProgress size={20} color="inherit" /> : <Play size={22} />}
              sx={{
                bgcolor: '#2196f3',
                color: 'white',
                py: 2.5,
                borderRadius: 2,
                fontWeight: 'bold',
                fontSize: { xs: '0.95rem', sm: '1.1rem' },
                '&:hover': { bgcolor: '#1976d2' },
                '&:disabled': { bgcolor: '#e0e0e0' },
                transition: 'all 0.3s',
                boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)'
              }}
            >
              Break In
            </Button>

            <Button
              variant="contained"
              onClick={() => handleClockAction('clock_out')}
              disabled={loading || !hotelId}
              startIcon={loading && actionType === 'clock_out' ? <CircularProgress size={20} color="inherit" /> : <LogOut size={22} />}
              sx={{
                bgcolor: '#f44336',
                color: 'white',
                py: 2.5,
                borderRadius: 2,
                fontWeight: 'bold',
                fontSize: { xs: '0.95rem', sm: '1.1rem' },
                '&:hover': { bgcolor: '#d32f2f' },
                '&:disabled': { bgcolor: '#e0e0e0' },
                transition: 'all 0.3s',
                boxShadow: '0 4px 12px rgba(244, 67, 54, 0.3)'
              }}
            >
              Clock Out
            </Button>
          </Box>

          {/* Última Ação */}
          {lastAction && (
            <Fade in={true}>
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  bgcolor: statusInfo.bg,
                  border: `2px solid ${statusInfo.color}40`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                  textAlign: 'center',
                  mt: 3
                }}
              >
                <Box sx={{ color: statusInfo.color }}>
                  {getActionIcon(lastAction.type, 28)}
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" fontWeight="500">
                    Last Action
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" sx={{ color: statusInfo.color }}>
                    {lastAction.type.replace('_', ' ').toUpperCase()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {lastAction.hotel_name} • {format(new Date(lastAction.timestamp), 'HH:mm')}
                  </Typography>
                </Box>
              </Box>
            </Fade>
          )}
        </Paper>

        {/* Histórico Recente */}
        <Paper elevation={2} sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3 }}>
          <Typography variant="h6" fontWeight="bold" mb={3} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <GalleryHorizontalEnd size={24} color="#667eea" />
            Recent Activity
          </Typography>

          {recentLogs.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
              <Clock size={48} style={{ opacity: 0.3, marginBottom: 16 }} />
              <Typography variant="body1">
                No recent activity
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {recentLogs.map((log) => (
                <Card
                  key={log.id}
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    borderLeft: `4px solid ${getActionColor(log.type)}`,
                    transition: 'all 0.3s',
                    '&:hover': {
                      boxShadow: 3,
                      transform: 'translateY(-2px)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: `${getActionColor(log.type)}20`,
                          color: getActionColor(log.type),
                          width: 48,
                          height: 48
                        }}
                      >
                        {getActionIcon(log.type, 24)}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" fontWeight="bold" sx={{ color: getActionColor(log.type) }}>
                          {log.type.replace('_', ' ').toUpperCase()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {format(new Date(log.timestamp), 'MMM d, yyyy • HH:mm:ss')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                          <MapPin size={12} />
                          {log.hotel_name}
                        </Typography>
                      </Box>
                      <CheckCircle size={24} color="#4caf50" />
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </Paper>
      </Container>

      {/* Animação de Sucesso */}
      {showSuccess && (
        <Fade in={showSuccess}>
          <Box
            sx={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 9999
            }}
          >
            <Box
              sx={{
                bgcolor: 'white',
                borderRadius: '50%',
                width: { xs: 100, sm: 120 },
                height: { xs: 100, sm: 120 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                animation: 'scaleIn 0.5s ease-out'
              }}
            >
              <CheckCircle size={60} color={actionType ? getActionColor(actionType) : '#4caf50'} />
            </Box>
          </Box>
        </Fade>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Animações CSS */}
      <style>
        {`
          @keyframes scaleIn {
            0% { transform: scale(0); }
            50% { transform: scale(1.1); }
            100% { transform: scale(1); }
          }
        `}
      </style>
    </Box>
  );
};

export default ClockInOutPage;